import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// COMPONENTS
import FileUpload from '../../components/FileUpload';
import FilesList from '../../components/FilesList';
import ErrorMes from '../../components/ErrorMessage';

// TYPES
import { SafetyChecklistAttachmentsAttachment, SafetyChecklistAttachmentDeleteCommand } from '../../api/models';
import { FileType } from '../../components/FileUpload';

interface AttachmentModalTypes {
	attachmentId: string;
	canEdit?: boolean;
}

const AttachmentModal: React.FC<AttachmentModalTypes> = ({ attachmentId, canEdit }) => {
	const [attachmentModalData, setAttachmentModalData] = React.useState<SafetyChecklistAttachmentsAttachment[]>([]);
	const [downloadAllUrl, setDownloadAllUrl] = React.useState<string>('');
	const [updateCount, setUpdateCount] = React.useState<number>(0);
	const [noFiles, setNoFiles] = React.useState<boolean>(true);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();

	const getSafetyChecklistsDetailAttachmentDetailData = async () => {
		const response = await apibridge.getSafetyChecklistAttachments(attachmentId);

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = {} } = response;

			if (result.attachments && result.attachments.length > 0) {
				setAttachmentModalData(result.attachments);

				setNoFiles(false);
			} else {
				setAttachmentModalData([]);

				setNoFiles(true);
			}

			setDownloadAllUrl(result.downloadAllUrl || '');
		}
	};

	React.useEffect(() => {
		getSafetyChecklistsDetailAttachmentDetailData();
	}, []);

	const fileUploadHandler = async (fileName: any, fileData: FileType[]) => {
		const response =
			fileName && (await apibridge.postSafetyChecklistAttachmentUpload({ id: attachmentId, fileName: fileName }));

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			getSafetyChecklistsDetailAttachmentDetailData();
		}

		setUpdateCount(updateCount + 1);
	};

	const fileRemoveHandler = async (file: string) => {
		const data: SafetyChecklistAttachmentDeleteCommand = {
			checklistItemId: attachmentId,
			id: file
		};

		const response = await apibridge.postSafetyChecklistAttachmentDelete(data);

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setUpdateCount(updateCount + 1);

			getSafetyChecklistsDetailAttachmentDetailData();
		}
	};

	return (
		<div className="mtop-0">
			<div className="form-group">
				<label htmlFor="additionalCommentInput" className="label">
					Add attachments
				</label>

				<FilesList
					onChange={(val: string) => fileRemoveHandler(val)}
					files={attachmentModalData}
					multiple={false}
					disabled={!canEdit}
				/>

				<div key={updateCount.toString()} className="form-field mbtom-14">
					<FileUpload
						keyValue={updateCount.toString()}
						name="Attachments"
						maxSizeMb={5}
						multiple={false}
						onChange={(val: string, files: FileType[]) => fileUploadHandler(val, files)}
						disabled={!canEdit}
					/>
				</div>
			</div>

			{!!formErrorMessage ? <ErrorMes message={formErrorMessage} /> : null}
			{noFiles ? (
				<>
					<div className="general-divider"></div>

					<p>No attachments have been uploaded</p>

					<div className="general-divider"></div>
				</>
			) : null}

			<div className="form-button-group w-100">
				{attachmentModalData && attachmentModalData.length > 0 && (
					<a
						className={`btn btn-secondary mtop-40 download-btn ${noFiles ? 'disabled' : ''} `}
						href={downloadAllUrl}
						download
					>
						Download {attachmentModalData.length > 1 ? 'all files' : ''}
					</a>
				)}
			</div>
		</div>
	);
};

export default AttachmentModal;
